import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import {
  ApiError,
  createWorkCost,
  deleteWorkCost,
  getWorkCosts,
  getWorkCostTypes,
  updateWorkCost,
  type WorkCost,
  type WorkCostPayload,
  type WorkCostType,
} from "../../../custos/custosApi";
import { getWorkers, type Worker } from "../../../trabalhadores/trabalhadoresApi";
import { getWorkBudgetChapters, type WorkBudgetChapter } from "../../../obras/obrasApi";
import { Modal } from "../../../components/Modal";
import { DetailsToggle } from "../../../components/DetailsToggle";
import { ListToolbar } from "../../../components/ListToolbar";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";

interface WorkCostsTabProps {
  workId: number;
  refreshKey?: number;
}

const MAO_DE_OBRA = "mao_de_obra";
const MATERIAL = "material";
const CONSUMO = "consumo";
const FATURA = "fatura";
const SUBEMPREITADA = "subempreitada";
const INDIRETO = "indireto";

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number): string => {
  if (!Number.isFinite(value)) return currencyFormatter.format(0);
  return currencyFormatter.format(value);
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

// Parse a localized/plain number string into a finite number, or null when blank/invalid.
const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

// Shape of the editable form (shared by the add card and the edit modal).
interface CostForm {
  workCostTypeId: string;
  description: string;
  costDate: string;
  amount: string;
  supplier: string;
  documentReference: string;
  workerId: string;
  hours: string;
  hourlyRate: string;
  quantity: string;
  unit: string;
  notes: string;
  budgetAreaName: string;
  budgetChapterName: string;
}

// The combobox encodes both area + chapter in a single option value, since a
// chapter name is only unique within its area. Empty value → no chapter.
const CHAPTER_SEP = "|||";
const chapterOptionValue = (area: string, chapter: string): string =>
  `${area}${CHAPTER_SEP}${chapter}`;

const emptyForm = (typeId: string): CostForm => ({
  workCostTypeId: typeId,
  description: "",
  costDate: todayInput(),
  amount: "",
  supplier: "",
  documentReference: "",
  workerId: "",
  hours: "",
  hourlyRate: "",
  quantity: "",
  unit: "",
  notes: "",
  budgetAreaName: "",
  budgetChapterName: "",
});

const formFromCost = (cost: WorkCost): CostForm => ({
  workCostTypeId: String(cost.workCostTypeId),
  description: cost.description ?? "",
  costDate: toInputDate(cost.costDate),
  amount: cost.amount !== null && cost.amount !== undefined ? String(cost.amount) : "",
  supplier: cost.supplier ?? "",
  documentReference: cost.documentReference ?? "",
  workerId: cost.workerId !== null && cost.workerId !== undefined ? String(cost.workerId) : "",
  hours: cost.hours !== null && cost.hours !== undefined ? String(cost.hours) : "",
  hourlyRate: cost.hourlyRate !== null && cost.hourlyRate !== undefined ? String(cost.hourlyRate) : "",
  quantity: cost.quantity !== null && cost.quantity !== undefined ? String(cost.quantity) : "",
  unit: cost.unit ?? "",
  notes: cost.notes ?? "",
  budgetAreaName: cost.budgetAreaName ?? "",
  budgetChapterName: cost.budgetChapterName ?? "",
});

// The live "Valor" preview for mão-de-obra (horas × tarifa).
const laborAmount = (form: CostForm): number => {
  const hours = parseNumber(form.hours) ?? 0;
  const rate = parseNumber(form.hourlyRate) ?? 0;
  return hours * rate;
};

// Compose the "Detalhe" column from whichever fields the row carries.
const composeDetail = (cost: WorkCost): string => {
  const parts: string[] = [];
  if (cost.typeCode === MAO_DE_OBRA) {
    if (cost.workerName) parts.push(cost.workerName);
    if (cost.hours !== null && cost.hours !== undefined && cost.hourlyRate !== null && cost.hourlyRate !== undefined) {
      parts.push(`${cost.hours} h × ${formatCurrency(cost.hourlyRate)}`);
    } else if (cost.hours !== null && cost.hours !== undefined) {
      parts.push(`${cost.hours} h`);
    }
  } else if (cost.typeCode === MATERIAL || cost.typeCode === CONSUMO) {
    if (cost.quantity !== null && cost.quantity !== undefined) {
      parts.push(`${cost.quantity}${cost.unit ? ` ${cost.unit}` : ""}`);
    } else if (cost.unit) {
      parts.push(cost.unit);
    }
  } else {
    if (cost.supplier) parts.push(cost.supplier);
    if (cost.documentReference) parts.push(cost.documentReference);
  }
  // Fall back to anything present if the type-specific compose was empty.
  if (parts.length === 0) {
    if (cost.supplier) parts.push(cost.supplier);
    if (cost.documentReference) parts.push(cost.documentReference);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
};

// Type-specific raw fields surfaced in the read-only "Ver detalhes" sub-row.
// Only fields actually present on the cost are returned, each plain-language
// labelled. These are the same details otherwise only visible in the edit form.
const detailRows = (cost: WorkCost): { label: string; value: string }[] => {
  const rows: { label: string; value: string }[] = [];
  const has = (v: unknown): boolean => v !== null && v !== undefined && v !== "";

  if (cost.typeCode === MAO_DE_OBRA) {
    if (has(cost.workerName)) rows.push({ label: "Trabalhador", value: String(cost.workerName) });
    if (has(cost.hours)) rows.push({ label: "Horas", value: `${cost.hours}` });
    if (has(cost.hourlyRate)) rows.push({ label: "Tarifa €/h", value: formatCurrency(cost.hourlyRate as number) });
  } else if (cost.typeCode === MATERIAL || cost.typeCode === CONSUMO) {
    if (has(cost.quantity)) rows.push({ label: "Quantidade", value: `${cost.quantity}` });
    if (has(cost.unit)) rows.push({ label: "Unidade", value: String(cost.unit) });
  }
  // Supplier / document reference can appear on invoices, subcontracts, etc.
  if (has(cost.supplier)) rows.push({ label: "Fornecedor", value: String(cost.supplier) });
  if (has(cost.documentReference)) rows.push({ label: "Nº documento / referência", value: String(cost.documentReference) });

  return rows;
};

export function WorkCostsTab({ workId, refreshKey }: WorkCostsTabProps) {
  const [costs, setCosts] = useState<WorkCost[]>([]);
  const [types, setTypes] = useState<WorkCostType[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [chapters, setChapters] = useState<WorkBudgetChapter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [filter, setFilter] = useState("");

  const [form, setForm] = useState<CostForm>(emptyForm(""));
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [editingCost, setEditingCost] = useState<WorkCost | null>(null);
  const [draft, setDraft] = useState<CostForm | null>(null);

  // Which list row has its read-only "Ver detalhes" sub-row expanded.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggleExpanded = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  const loadCosts = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setCosts(await getWorkCosts(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os custos.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  // Re-fetch silently when an imputation elsewhere bumps the refresh key,
  // so the Custos tab stays in sync if it's already open.
  useEffect(() => {
    if (refreshKey === undefined) return;
    loadCosts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [typeList, workerList, chapterList] = await Promise.all([
          getWorkCostTypes(),
          getWorkers(),
          getWorkBudgetChapters(workId),
        ]);
        if (cancelled) return;
        setTypes(typeList);
        setWorkers(workerList);
        setChapters(chapterList);
      } catch {
        // Non-blocking: costs still render; selects just have fewer options.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workId]);

  const defaultTypeId = useMemo(() => types[0]?.id ?? null, [types]);

  useEffect(() => {
    if (form.workCostTypeId === "" && defaultTypeId !== null) {
      setForm((prev) => ({ ...prev, workCostTypeId: String(defaultTypeId) }));
    }
  }, [defaultTypeId, form.workCostTypeId]);

  const activeWorkers = useMemo(() => workers.filter((w) => w.isActive), [workers]);

  const workerOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      activeWorkers.map((worker) => ({
        value: String(worker.id),
        label: worker.name,
      })),
    [activeWorkers],
  );

  // Sort client-side (area, then chapter); the server order may not survive SQL translation.
  const sortedChapters = useMemo(
    () =>
      [...chapters].sort(
        (a, b) =>
          a.areaName.localeCompare(b.areaName, APP_LOCALE) ||
          a.chapterName.localeCompare(b.chapterName, APP_LOCALE),
      ),
    [chapters],
  );

  const codeForTypeId = useCallback(
    (typeId: string): string => {
      const found = types.find((t) => String(t.id) === typeId);
      return found?.code ?? "";
    },
    [types],
  );

  const summary = useMemo(() => {
    let total = 0;
    const byType = new Map<string, { name: string; total: number }>();
    for (const cost of costs) {
      const amount = Number.isFinite(cost.amount) ? cost.amount : 0;
      total += amount;
      const existing = byType.get(cost.typeCode);
      if (existing) {
        existing.total += amount;
      } else {
        byType.set(cost.typeCode, { name: cost.typeName, total: amount });
      }
    }
    return { total, byType: Array.from(byType.values()), count: costs.length };
  }, [costs]);

  // Client-side filter: case-insensitive substring across the listed text columns.
  const normalizedFilter = filter.trim().toLowerCase();
  const shown = useMemo(() => {
    if (normalizedFilter === "") return costs;
    return costs.filter((cost) =>
      [
        cost.description,
        cost.typeName,
        cost.typeCode,
        cost.supplier,
        cost.documentReference,
        cost.budgetChapterName,
        composeDetail(cost),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedFilter)),
    );
  }, [costs, normalizedFilter]);

  // Total of the currently visible (filtered) rows, for the compact toolbar summary.
  const shownTotal = useMemo(
    () => shown.reduce((sum, cost) => sum + (Number.isFinite(cost.amount) ? cost.amount : 0), 0),
    [shown],
  );

  const resetForm = () => {
    setForm(emptyForm(defaultTypeId !== null ? String(defaultTypeId) : ""));
  };

  // Create entry-point: clear the form and open the (create) modal.
  const openCreate = () => {
    resetForm();
    setError(null);
    setIsCreateOpen(true);
  };

  // Close whichever variant of the form modal is open (create or edit).
  const closeForm = () => {
    setIsCreateOpen(false);
    setEditingCost(null);
    setDraft(null);
  };

  // Build the payload from a form for a given resolved type code.
  const buildPayload = (source: CostForm, typeCode: string): WorkCostPayload => {
    const isLabor = typeCode === MAO_DE_OBRA;
    const isMaterial = typeCode === MATERIAL || typeCode === CONSUMO;
    const isInvoice = typeCode === FATURA;
    const hasSupplier = isInvoice || typeCode === SUBEMPREITADA || typeCode === INDIRETO;

    const amount = isLabor ? laborAmount(source) : parseNumber(source.amount) ?? 0;

    return {
      workId,
      workCostTypeId: Number(source.workCostTypeId),
      description: source.description.trim(),
      costDate: source.costDate !== "" ? source.costDate : null,
      amount,
      supplier: hasSupplier && source.supplier.trim() !== "" ? source.supplier.trim() : null,
      documentReference:
        isInvoice && source.documentReference.trim() !== "" ? source.documentReference.trim() : null,
      workerId: isLabor && source.workerId !== "" ? Number(source.workerId) : null,
      hours: isLabor ? parseNumber(source.hours) : null,
      hourlyRate: isLabor ? parseNumber(source.hourlyRate) : null,
      quantity: isMaterial ? parseNumber(source.quantity) : null,
      unit: isMaterial && source.unit.trim() !== "" ? source.unit.trim() : null,
      notes: source.notes.trim() !== "" ? source.notes.trim() : null,
      budgetAreaName: source.budgetAreaName !== "" ? source.budgetAreaName : null,
      budgetChapterName: source.budgetChapterName !== "" ? source.budgetChapterName : null,
    };
  };

  // Validate a form; returns an error message or null when valid.
  const validate = (source: CostForm, typeCode: string): string | null => {
    if (source.workCostTypeId === "") return "Selecione o tipo de custo.";
    if (!source.description.trim()) return "A descrição é obrigatória.";
    if (!source.costDate) return "A data é obrigatória.";
    const amount = typeCode === MAO_DE_OBRA ? laborAmount(source) : parseNumber(source.amount);
    if (amount === null || !Number.isFinite(amount) || amount < 0) {
      return "Indique um valor válido (≥ 0).";
    }
    return null;
  };

  const handleAdd = async () => {
    if (isSaving) return;
    const typeCode = codeForTypeId(form.workCostTypeId);
    const validationError = validate(form, typeCode);
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const created = await createWorkCost(buildPayload(form, typeCode));
      if (!created.typeCode) {
        await loadCosts(true);
      } else {
        setCosts((prev) =>
          [...prev, created].sort((a, b) => {
            const cmp = new Date(b.costDate).getTime() - new Date(a.costDate).getTime();
            return cmp !== 0 ? cmp : b.id - a.id;
          }),
        );
      }
      resetForm();
      setIsCreateOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível adicionar o custo.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (costId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este custo?")) return;
    setDeletingId(costId);
    setError(null);
    try {
      await deleteWorkCost(costId);
      setCosts((prev) => prev.filter((item) => item.id !== costId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setCosts((prev) => prev.filter((item) => item.id !== costId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o custo.");
        loadCosts(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (cost: WorkCost) => {
    setError(null);
    setIsCreateOpen(false);
    setEditingCost(cost);
    setDraft(formFromCost(cost));
  };

  const saveEdit = async () => {
    if (!editingCost || !draft) return;
    const typeCode = codeForTypeId(draft.workCostTypeId);
    const validationError = validate(draft, typeCode);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      const updated = await updateWorkCost(editingCost.id, {
        id: editingCost.id,
        ...buildPayload(draft, typeCode),
      });
      if (!updated.typeCode) {
        await loadCosts(true);
      } else {
        setCosts((prev) =>
          prev
            .map((item) => (item.id === updated.id ? updated : item))
            .sort((a, b) => {
              const cmp = new Date(b.costDate).getTime() - new Date(a.costDate).getTime();
              return cmp !== 0 ? cmp : b.id - a.id;
            }),
        );
      }
      setEditingCost(null);
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o custo.");
    }
  };

  // Renders the type-aware extra fields for either the add form or the edit modal.
  const renderTypeFields = (
    source: CostForm,
    update: (patch: Partial<CostForm>) => void,
  ) => {
    const typeCode = codeForTypeId(source.workCostTypeId);

    if (typeCode === MAO_DE_OBRA) {
      return (
        <>
          <SearchableSelect<string>
            label="Trabalhador"
            labelClassName="text-sm text-secondary-text"
            labelStyle={{}}
            value={source.workerId === "" ? null : source.workerId}
            onChange={(next) => {
              const value = next ?? "";
              const worker = activeWorkers.find((w) => String(w.id) === value);
              const rate =
                worker && worker.hourlyCost !== null && worker.hourlyCost !== undefined
                  ? String(worker.hourlyCost)
                  : source.hourlyRate;
              update({ workerId: value, hourlyRate: rate });
            }}
            options={workerOptions}
            placeholder="—"
            searchPlaceholder="Procurar trabalhador…"
          />
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Horas
            <input
              type="number"
              min={0}
              step={0.5}
              value={source.hours}
              onChange={(event) => update({ hours: event.target.value })}
              placeholder="0"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Tarifa €/h
            <input
              type="number"
              min={0}
              step={0.01}
              value={source.hourlyRate}
              onChange={(event) => update({ hourlyRate: event.target.value })}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Valor
            <input
              type="text"
              readOnly
              value={formatCurrency(laborAmount(source))}
              className="px-3 py-2 border border-border rounded bg-muted text-foreground"
            />
          </label>
        </>
      );
    }

    if (typeCode === MATERIAL || typeCode === CONSUMO) {
      return (
        <>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Quantidade
            <input
              type="number"
              min={0}
              step={0.001}
              value={source.quantity}
              onChange={(event) => update({ quantity: event.target.value })}
              placeholder="0"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Unidade
            <input
              type="text"
              maxLength={20}
              value={source.unit}
              onChange={(event) => update({ unit: event.target.value })}
              placeholder="un, m², kg…"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Valor €
            <input
              type="number"
              min={0}
              step={0.01}
              value={source.amount}
              onChange={(event) => update({ amount: event.target.value })}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
        </>
      );
    }

    if (typeCode === FATURA) {
      return (
        <>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Fornecedor
            <input
              type="text"
              maxLength={200}
              value={source.supplier}
              onChange={(event) => update({ supplier: event.target.value })}
              placeholder="Fornecedor / origem"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Nº documento / referência
            <input
              type="text"
              maxLength={100}
              value={source.documentReference}
              onChange={(event) => update({ documentReference: event.target.value })}
              placeholder="Nº fatura / referência"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Valor €
            <input
              type="number"
              min={0}
              step={0.01}
              value={source.amount}
              onChange={(event) => update({ amount: event.target.value })}
              placeholder="0,00"
              className="px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </label>
        </>
      );
    }

    // subempreitada / indireto (and any other) — supplier (optional) + amount.
    return (
      <>
        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Fornecedor
          <input
            type="text"
            maxLength={200}
            value={source.supplier}
            onChange={(event) => update({ supplier: event.target.value })}
            placeholder="Fornecedor (opcional)"
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-secondary-text">
          Valor €
          <input
            type="number"
            min={0}
            step={0.01}
            value={source.amount}
            onChange={(event) => update({ amount: event.target.value })}
            placeholder="0,00"
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
          />
        </label>
      </>
    );
  };

  const chapterOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "— Sem capítulo —" },
      ...sortedChapters.map((chapter) => ({
        value: chapterOptionValue(chapter.areaName, chapter.chapterName),
        label: `${chapter.areaName} — ${chapter.chapterName}`,
      })),
    ],
    [sortedChapters],
  );

  // Optional "Capítulo" select, shared by the add form and the edit modal.
  const renderChapterField = (
    source: CostForm,
    update: (patch: Partial<CostForm>) => void,
  ) => (
    <SearchableSelect<string>
      label="Capítulo"
      labelClassName="text-sm text-secondary-text"
      labelStyle={{}}
      value={
        source.budgetChapterName
          ? chapterOptionValue(source.budgetAreaName, source.budgetChapterName)
          : ""
      }
      onChange={(next) => {
        const value = next ?? "";
        if (value === "") {
          update({ budgetAreaName: "", budgetChapterName: "" });
          return;
        }
        const [area, chapter] = value.split(CHAPTER_SEP);
        update({ budgetAreaName: area, budgetChapterName: chapter });
      }}
      options={chapterOptions}
      allowClear={false}
      searchPlaceholder="Procurar capítulo…"
    />
  );

  const updateForm = (patch: Partial<CostForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const updateDraft = (patch: Partial<CostForm>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  // Shared form body for both the create and edit modals (same fields, same
  // type-driven extras), parameterized by which form state it reads/writes.
  const renderFormBody = (
    source: CostForm,
    update: (patch: Partial<CostForm>) => void,
  ) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Tipo *
        <select
          value={source.workCostTypeId}
          onChange={(event) => update({ workCostTypeId: event.target.value })}
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
        >
          {types.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text">
        Data *
        <input
          type="date"
          value={source.costDate}
          onChange={(event) => update({ costDate: event.target.value })}
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
        Descrição *
        <input
          type="text"
          maxLength={300}
          value={source.description}
          onChange={(event) => update({ description: event.target.value })}
          placeholder="Descrição do custo"
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
        />
      </label>
      {renderTypeFields(source, update)}
      {renderChapterField(source, update)}
      <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
        Notas
        <textarea
          value={source.notes}
          maxLength={1000}
          rows={3}
          onChange={(event) => update({ notes: event.target.value })}
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
        />
      </label>
    </div>
  );

  return (
    <div className="space-y-6">
      {error && <div className="border border-border bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar custos…"
        summary={
          <span className="text-secondary-text text-sm">
            {shown.length} custo(s) · Total realizado {formatCurrency(shownTotal)}
          </span>
        }
      >
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Novo custo
        </button>
      </ListToolbar>

      {summary.byType.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.byType.map((entry) => (
            <span key={entry.name} className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20" style={{ borderRadius: "var(--radius)" }}>
              {entry.name} {formatCurrency(entry.total)}
            </span>
          ))}
        </div>
      )}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="w-full" style={{ minWidth: "880px" }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 56 }}></th>
              <th className="px-4 py-3" style={{ width: "120px" }}>Data</th>
              <th className="px-4 py-3" style={{ width: "140px" }}>Tipo</th>
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3">Detalhe</th>
              <th className="px-4 py-3 text-right" style={{ width: "140px" }}>Valor</th>
              <th className="px-4 py-3" style={{ width: "140px" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-text">{filter !== "" ? "Sem resultados para o filtro." : "Sem custos registados. Use \"+ Novo custo\" para adicionar o primeiro."}</td></tr>
            ) : shown.map((cost) => (
              <Fragment key={cost.id}>
                <tr className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">
                    <DetailsToggle open={expandedId === cost.id} onToggle={() => toggleExpanded(cost.id)} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(cost.costDate)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20" style={{ borderRadius: "var(--radius)" }}>
                      {cost.typeName}
                    </span>
                  </td>
                  <td className="px-4 py-3 break-words">{cost.description || "—"}</td>
                  <td className="px-4 py-3 break-words">{composeDetail(cost)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(cost.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap items-center">
                      <button className="text-primary underline" type="button" onClick={() => openEdit(cost)}>Editar</button>
                      <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === cost.id} onClick={() => handleDelete(cost.id)}>{deletingId === cost.id ? "A eliminar…" : "Eliminar"}</button>
                    </div>
                  </td>
                </tr>
                {expandedId === cost.id && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={7} className="px-4 py-3 bg-muted">
                      <div className="space-y-2 text-sm">
                        <div><span className="text-secondary-text">Notas: </span>{cost.notes || "—"}</div>
                        <div><span className="text-secondary-text">Capítulo: </span>{[cost.budgetAreaName, cost.budgetChapterName].filter(Boolean).join(" · ") || "—"}</div>
                        {detailRows(cost).map((detail) => (
                          <div key={detail.label}><span className="text-secondary-text">{detail.label}: </span>{detail.value}</div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {(isCreateOpen || (editingCost && draft)) && (
        <Modal
          title={editingCost ? "Editar custo" : "Novo custo"}
          onClose={closeForm}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button type="button" onClick={closeForm} className="px-4 py-2 min-h-[44px] border border-border" style={{ borderRadius: "var(--radius-button)" }}>Cancelar</button>
              <button
                type="button"
                disabled={!editingCost && isSaving}
                onClick={editingCost ? saveEdit : handleAdd}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {!editingCost && isSaving ? "A adicionar…" : "Guardar"}
              </button>
            </>
          }
        >
          {editingCost && draft
            ? renderFormBody(draft, updateDraft)
            : renderFormBody(form, updateForm)}
        </Modal>
      )}
    </div>
  );
}
